import React, { useEffect, useState } from 'react';
import { apiService } from '@/services/api';

/**
 * "My bottles" — a guest's kept bottles at this venue (bottle-keep). Shows bottles tied to THIS device
 * (same phone, next visit) or to a registered profile — matched automatically server-side. A different
 * device / number is handled by staff (they look it up by name / phone / hint). No sign-in needed.
 */
const LEVEL_LABEL: Record<string, string> = { full: 'Full', three_quarter: '¾ left', half: '½ left', quarter: '¼ left', almost_empty: 'Almost empty' };
const REQUEST_LABEL: Record<string, string> = { new: '🔔 Requested — staff notified', approved: '✓ Accepted — coming up', preparing: '👨‍🍳 Preparing your bottle', in_progress: '🏃 On the way!', served: '🍾 At your table — enjoy! 🎉' };
const daysLeft = (iso?: string | null) => { if (!iso) return null; return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000); };

const MyBottlesModal: React.FC<{ bookingId: string; onClose: () => void; onChanged?: (n: number) => void }> = ({ bookingId, onClose, onChanged }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const fetch = (first = false) => apiService.getMyKeptBottles(bookingId)
      .then((list) => { if (!live) return; setRows(list || []); onChanged?.(list?.length || 0); })
      .finally(() => { if (live && first) setLoading(false); });
    fetch(true);
    // Poll so a staff action (served / request cleared) reflects here within a few seconds.
    const poll = window.setInterval(() => fetch(false), 5000);
    return () => { live = false; window.clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const request = async (id: string) => {
    setBusyId(id);
    try { await apiService.requestKeptBottle(bookingId, id); setRows((r) => r.map((b) => b.id === id ? { ...b, serveRequestedAt: new Date().toISOString() } : b)); }
    catch { /* ignore */ } finally { setBusyId(null); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <span style={{ fontWeight: 900, fontSize: '1.1rem' }}>🍾 My bottles</span>
          <button style={x} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={body}>
          {loading ? <div style={muted}>Loading…</div> : rows.length === 0 ? (
            <div style={muted}>No bottles kept for you here yet. If you don’t finish a bottle, ask staff to keep it — it’ll show here on your next visit from this phone. 🍾</div>
          ) : rows.map((b) => {
            const dl = daysLeft(b.expiresAt);
            return (
              <div key={b.id} style={card}>
                <div style={photo}>{b.photoUrl ? <img src={b.photoUrl} alt="" style={img} /> : <span style={{ fontSize: 30 }}>🍾</span>}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{b.itemName || 'Bottle'}</div>
                  <div style={metaRow}>
                    <span style={pill}>{LEVEL_LABEL[b.level] || b.level}</span>
                    {dl != null && <span style={{ ...pill, ...(dl <= 3 ? pillWarn : {}) }}>{dl > 0 ? `${dl} day${dl === 1 ? '' : 's'} left` : 'expiring'}</span>}
                  </div>
                  {(b.serveRequestedAt || b.requestStatus === 'served')
                    ? <div style={requested}>{REQUEST_LABEL[b.requestStatus as string] || REQUEST_LABEL.new}</div>
                    : <button style={btn} disabled={busyId === b.id} onClick={() => request(b.id)}>{busyId === b.id ? 'Sending…' : 'Ask staff to bring it'}</button>}
                </div>
              </div>
            );
          })}
          <div style={footnote}>Changed phones? Just ask staff — they’ll find your bottle by your name or number.</div>
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(2,6,23,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const sheet: React.CSSProperties = { background: '#fff', width: '100%', maxWidth: 520, borderRadius: '18px 18px 0 0', maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const head: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid #eef2f7', color: '#0f172a' };
const x: React.CSSProperties = { width: 34, height: 34, borderRadius: 10, border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 15 };
const body: React.CSSProperties = { padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, color: '#0f172a' };
const muted: React.CSSProperties = { color: '#64748b', textAlign: 'center', padding: 20, lineHeight: 1.5 };
const card: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'center', border: '1px solid #eef2f7', borderRadius: 14, padding: 12 };
const photo: React.CSSProperties = { width: 60, height: 60, borderRadius: 12, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' };
const img: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };
const metaRow: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 8px' };
const pill: React.CSSProperties = { background: '#f1f5f9', color: '#475569', borderRadius: 9999, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 800 };
const pillWarn: React.CSSProperties = { background: '#fef3c7', color: '#92400e' };
const btn: React.CSSProperties = { border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', fontWeight: 800, padding: '9px 14px', cursor: 'pointer', fontSize: '0.88rem' };
const requested: React.CSSProperties = { color: '#16a34a', fontWeight: 800, fontSize: '0.85rem' };
const footnote: React.CSSProperties = { color: '#94a3b8', textAlign: 'center', fontSize: '0.78rem', padding: '4px 8px 8px' };

export default MyBottlesModal;
